import React from 'react';
import { BoltIcon, HeartIcon, ClockIcon } from '@heroicons/react/24/outline';

export type LeaderboardMode = 'rating' | 'survival' | 'rush';
export type TimePeriod = 'all_time' | 'this_month' | 'this_year';
export type RushDuration = 5 | 10 | 20 | 30;

interface LeaderboardFiltersProps {
  mode: LeaderboardMode;
  timePeriod: TimePeriod;
  rushDuration: RushDuration;
  onModeChange: (mode: LeaderboardMode) => void;
  onTimePeriodChange: (period: TimePeriod) => void;
  onRushDurationChange: (duration: RushDuration) => void;
}

const LeaderboardFilters: React.FC<LeaderboardFiltersProps> = ({
  mode,
  timePeriod,
  rushDuration,
  onModeChange,
  onTimePeriodChange,
  onRushDurationChange
}) => {
  return (
    <div className="space-y-4">
      {/* Mode Selector */}
      <div>
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          Leaderboard Type
        </h3>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => onModeChange('rating')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center ${
              mode === 'rating'
                ? 'bg-mango-500 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            <BoltIcon className="h-5 w-5 mr-2" />
            Rating
          </button>
          <button
            onClick={() => onModeChange('survival')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center ${
              mode === 'survival'
                ? 'bg-mango-500 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            <HeartIcon className="h-5 w-5 mr-2" />
            Survival
          </button>
          <button
            onClick={() => onModeChange('rush')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center ${
              mode === 'rush'
                ? 'bg-mango-500 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            <ClockIcon className="h-5 w-5 mr-2" />
            Rush Mode
          </button>
        </div>
      </div>

      {/* Time Period Filter */}
      <div>
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          Time Period
        </h3>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => onTimePeriodChange('all_time')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              timePeriod === 'all_time'
                ? 'bg-blue-500 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            All Time
          </button>
          <button
            onClick={() => onTimePeriodChange('this_month')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              timePeriod === 'this_month'
                ? 'bg-blue-500 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            This Month
          </button>
          <button
            onClick={() => onTimePeriodChange('this_year')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              timePeriod === 'this_year'
                ? 'bg-blue-500 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            This Year
          </button>
        </div>
      </div>

      {/* Rush Duration Filter (only shown for rush mode) */}
      {mode === 'rush' && (
        <div>
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Duration
          </h3>
          <div className="flex flex-wrap gap-2">
            {[5, 10, 20, 30].map((duration) => (
              <button
                key={duration}
                onClick={() => onRushDurationChange(duration as RushDuration)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  rushDuration === duration
                    ? 'bg-purple-500 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {duration} min
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaderboardFilters;
